import type { EventData, MapLayerEventType, MapLayerMouseEvent, MapLayerTouchEvent } from 'mapbox-gl';
import { useCallback, useEffect } from 'react';

export type MapMouseMoveEvent = (MapLayerMouseEvent | MapLayerTouchEvent) & EventData;

interface MapEventProps {
  map?: mapboxgl.Map;
  labelName: string;
  onMouseMove: (e: MapMouseMoveEvent) => void;
  onMouseLeave: (e: MapMouseMoveEvent) => void;
  mouseMoveEvent?: keyof MapLayerEventType;
  mouseLeaveEvent?: keyof MapLayerEventType;
}

export const useMapMouseMoveEvent = ({
  map,
  labelName,
  onMouseMove,
  onMouseLeave,
  mouseMoveEvent = 'mousemove',
  mouseLeaveEvent = 'mouseleave'
}: MapEventProps): void => {
  const handleMouseMove = useCallback(
    (e: MapMouseMoveEvent) => {
      if (!map) return;

      onMouseMove?.(e);
    },
    [map, onMouseMove]
  );

  const handleMouseLeave = useCallback(
    (e: MapMouseMoveEvent) => {
      if (!map) return;

      onMouseLeave?.(e);
    },
    [map, onMouseLeave]
  );

  useEffect(() => {
    if (!map) return () => undefined;

    map.on(mouseMoveEvent, labelName, handleMouseMove);
    map.on(mouseLeaveEvent, labelName, handleMouseLeave);

    return () => {
      map.off(mouseMoveEvent, labelName, handleMouseMove);
      map.off(mouseLeaveEvent, labelName, handleMouseLeave);
    };
  }, [map, labelName, handleMouseMove, handleMouseLeave]);
};
